<template>
    <EChart v-if="isVisible"
        class="cashflow_chart"
        :option="chartOption"
        @chart-rendered="onChartRendered" />
</template>

<script>
import EChart from "@/components/charts/EChart.vue";
import numberFormatter from "@/utils/numberFormatter.js";
import { parseToMoney } from "@/utils/currency";
import moment from "moment";

export default {
    name: "CardGraphCashFlow",
    components: { EChart },
    props: {
        chartObject: { type: Object, default: null },
        isVisible: { type: Boolean, default: false },
    },
    data() {
        // notRenderedYet replica el gate del Chart.js original: el emit
        // `onChartRendered` se dispara exactamente una vez por ciclo de
        // visibilidad. ECharts fires `finished` en cada animación, así que el
        // flag es necesario para no spamear al parent.
        return { notRenderedYet: true };
    },
    computed: {
        chartOption() {
            if (!this.chartObject) return {};
            const { labels, balance, pagar, cobrar } = this.chartObject;
            // X axis time-based: pair cada label con su valor. Coincide con la
            // semántica original (`xAxes[].type: 'time', unit: 'week'`).
            const series = [
                {
                    name: "Cuentas Bancarias",
                    type: "line",
                    showSymbol: true,
                    symbolSize: 7,
                    lineStyle: { color: "#5A4CD3", width: 2.3 },
                    itemStyle: { color: "#5A4CD3" },
                    data: labels.map((d, i) => [d, balance[i]]),
                },
                {
                    name: "Por pagar",
                    type: "bar",
                    stack: "now",
                    // Ancho fijo (no max): en eje time sin categorías ECharts
                    // colapsa los bars a 2-3px porque "no sabe" cuánto espacio
                    // hay entre data points. 20px es legible sin solaparse en
                    // densidades de hasta ~16 semanas, que es el rango típico.
                    barWidth: 20,
                    itemStyle: {
                        color: "#E1AFCF",
                        borderColor: "#B53886",
                        borderWidth: 1.8,
                    },
                    data: labels.map((d, i) => [d, pagar[i]]),
                },
                {
                    name: "Por cobrar",
                    type: "bar",
                    stack: "now",
                    barWidth: 20,
                    itemStyle: {
                        color: "#99DDDE",
                        borderColor: "#00A9AD",
                        borderWidth: 1.8,
                    },
                    data: labels.map((d, i) => [d, cobrar[i]]),
                },
            ];
            return {
                // bottom: 56 deja aire bajo el plot para que la leyenda
                // (bottom: 0) no le coma altura al chart. top: 16 ajusta los
                // labels del eje Y (que ECharts puede pintar fuera del grid).
                grid: { left: 12, right: 16, top: 16, bottom: 56, containLabel: true },
                xAxis: {
                    type: "time",
                    // hideOverlap: ECharts decide qué labels mostrar/saltar
                    // según el ancho disponible. Sin esto, todos los ticks
                    // semanales colisionan en pantallas medias.
                    axisLabel: {
                        hideOverlap: true,
                        formatter: (value) => moment(value).format("DD-MMM"),
                        color: "#6A6B6D",
                    },
                    splitLine: { lineStyle: { color: "#EFEFEF" } },
                },
                yAxis: {
                    type: "value",
                    axisLabel: {
                        formatter: (value) => numberFormatter(value, 2),
                        color: "#272727",
                    },
                    splitLine: {
                        lineStyle: { color: "#EFEFEF" },
                    },
                    axisLine: {
                        show: true,
                        lineStyle: { color: "#272727", width: 1.5 },
                    },
                },
                tooltip: {
                    trigger: "axis",
                    axisPointer: { type: "cross" },
                    formatter: (params) => {
                        const title = moment(params[0].axisValue).format("DD-MMM-YYYY");
                        const lines = params
                            .map((p) => `${p.seriesName}: ${parseToMoney(p.value[1])}`)
                            .join("<br>");
                        return `<strong>${title}</strong><br>${lines}`;
                    },
                },
                legend: { bottom: 0, textStyle: { color: "#272727" } },
                series,
            };
        },
    },
    watch: {
        isVisible(newVal) {
            // El parent recicla el componente con v-if cuando se cambia de tab.
            // Reset del flag para que el primer render del nuevo ciclo dispare
            // el emit (los consumers — Consolidation — lo usan como signal de
            // "el chart ya está visible y pintado").
            if (!newVal) this.notRenderedYet = true;
        },
    },
    methods: {
        onChartRendered() {
            if (!this.notRenderedYet) return;
            this.notRenderedYet = false;
            this.$emit("onChartRendered");
        },
    },
};
</script>

<style scoped>
.cashflow_chart {
    width: 100%;
    height: 100%;
    /* // 240px como piso defensivo: si el parent no estira (p.ej. cuando este
    // chart se inserta en un layout sin altura definida), seguimos teniendo
    // algo visible en vez de un canvas de 0px. El consumer actual
    // (Consolidation.vue) flex-stretchea, así que en práctica el chart toma
    // la altura del row completo. */
    min-height: 240px;
}
</style>
